<template>
  <div class="flex justify-end gap-2">
    <CButton
      v-if="!hideShare"
      v-tooltip="t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.actions.buttons.tooltips.share')"
      circle
      :loading="isSharing"
      @click="emit('share')"
    >
      <template #icon>
        <CIcon class="mr-1" icon="link" size="16px" />
      </template>
    </CButton>

    <CButton
      v-if="!hideDelete"
      v-tooltip="t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.actions.buttons.tooltips.delete')"
      circle
      :loading="isDeleting"
      secondary
      type="error"
      @click="emit('delete')"
    >
      <template #icon>
        <CIcon icon="trash" size="16px" />
      </template>
    </CButton>

    <CDropdown
      :options="getActionsMenu()"
      trigger="click"
      @select="(v: string) => handleActionSelect(v)"
      @update:show="handleDropdownVisibility"
    >
      <CButton
        v-tooltip="t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.actions.buttons.tooltips.actions')"
        circle
      >
        <template #icon>
          <CIcon icon="ellipsis" size="16px" />
        </template>
      </CButton>
    </CDropdown>
  </div>
</template>

<script setup lang="tsx">
import { computed, inject, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useOnline } from '@vueuse/core'

import CButton from '@lib/components/CButton/CButton.vue'
import CDropdown from '@lib/components/CDropdown/CDropdown.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import tooltipDirective from '@lib/directives/tooltip'
import useThemeVars from '@lib/composables/useThemeVars'
import type { DropdownMixedOption } from 'naive-ui/es/dropdown/src/interface'
import useCanvasLocks from '@lib/composables/useCanvasLocks'
import type { CanvasRetrieve } from '@openapi'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { CDP_EVENT_TYPE } from '@lib/constants/cdp.const'

const props = defineProps<{
  canvasId: CanvasRetrieve['id']
  hideCopy?: boolean
  hideEdit?: boolean
  hideDelete?: boolean
  hideShare?: boolean
  hideViewNewTab?: boolean
  hideOwnerReassign?: boolean
  hideLock?: boolean
  isCopying: boolean
  isDeleting: boolean
  isDownloading: boolean
  isSharing: boolean
  isReassigningOwner?: boolean
  downloadStatus?: 'not_started' | 'in_progress' | 'completed' | 'error'
  pdfUrl?: string
  pptxUrl?: string
  isExpired?: boolean
  track?: (eventName: string, payload: any) => void
}>()

const emit = defineEmits<{
  duplicate: []
  view: []
  viewNewTab: []
  delete: []
  share: []
  edit: []
  copy: []
  lock: []
  unlock: []
  ownerReassign: []
  downloadCanvas: [fileFormat?: 'pdf' | 'pptx']
  startAsyncDownload: []
  downloadPdf: []
  downloadPptx: []
  dropdownOpen: []
}>()

const { t } = useI18n()
const themeVars = useThemeVars()
const online = useOnline()
const { isCanvasLocked, isLockedByCurrentUser, isOrgAdmin } = useCanvasLocks()
const isLocked = computed(() => isCanvasLocked(props.canvasId))
const isLockedByMe = computed(() => isLockedByCurrentUser(props.canvasId))
const vTooltip = tooltipDirective

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const FONT_SIZE_CLASS = 'font-size-4'

function handleDropdownVisibility(visible: boolean) {
  if (visible) {
    // Emit event to parent to fetch latest canvas context
    emit('dropdownOpen')
  }
}

function handleActionSelect(action: string) {
  if (launchDarkly.value.enable_scale_content && props.downloadStatus) {
    // Handle async download when experiments enabled
    if (action === 'downloadCanvas') {
      if (props.downloadStatus === 'not_started' || props.downloadStatus === 'completed') {
        // Track canvas download initiation
        if (props.track) {
          props.track(CDP_EVENT_TYPE.canvasDownloaded, {
            canvas_id: props.canvasId,
            file_format: 'async',
            action_type: props.downloadStatus === 'completed' ? 'regenerate' : 'initial',
          })
        }
        // For both initial download and regenerate, start async download
        emit('startAsyncDownload')
      }
    } else if (action === 'downloadCanvas-pdf') {
      if (props.downloadStatus === 'completed' && props.pdfUrl) {
        // Track PDF download
        if (props.track) {
          props.track(CDP_EVENT_TYPE.canvasDownloaded, {
            canvas_id: props.canvasId,
            file_format: 'pdf',
            action_type: 'download_ready_file',
          })
        }
        emit('downloadPdf')
      }
    } else if (action === 'downloadCanvas-pptx') {
      if (props.downloadStatus === 'completed' && props.pptxUrl) {
        // Track PPTX download
        if (props.track) {
          props.track(CDP_EVENT_TYPE.canvasDownloaded, {
            canvas_id: props.canvasId,
            file_format: 'pptx',
            action_type: 'download_ready_file',
          })
        }
        emit('downloadPptx')
      }
    } else {
      emit(action as any)
    }
  } else {
    // Legacy download behavior
    if (action === 'downloadCanvas-pdf') {
      // Track PDF download (legacy)
      if (props.track) {
        props.track(CDP_EVENT_TYPE.canvasDownloaded, {
          canvas_id: props.canvasId,
          file_format: 'pdf',
          action_type: 'legacy_download',
        })
      }
      emit('downloadCanvas', 'pdf')
    } else if (action === 'downloadCanvas-pptx') {
      // Track PPTX download (legacy)
      if (props.track) {
        props.track(CDP_EVENT_TYPE.canvasDownloaded, {
          canvas_id: props.canvasId,
          file_format: 'pptx',
          action_type: 'legacy_download',
        })
      }
      emit('downloadCanvas', 'pptx')
    } else {
      emit(action as any)
    }
  }
}

function getActionsMenu() {
  const actionsMenu: DropdownMixedOption[] = [
    {
      label: () => (
        <div class={FONT_SIZE_CLASS}>
          {t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.actions.view')}
        </div>
      ),
      key: 'view',
      icon: () => <CIcon icon="eye" />,
    },
  ]

  if (!props.hideEdit) {
    actionsMenu.push({
      label: () => (
        <div class={FONT_SIZE_CLASS}>
          {t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.actions.edit')}
        </div>
      ),
      key: 'edit',
      icon: () => <CIcon icon="edit" />,
    })
  }

  if (!props.hideShare) {
    actionsMenu.push({
      label: () => (
        <div class={FONT_SIZE_CLASS}>
          {t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.actions.share')}
        </div>
      ),
      key: 'share',
      disabled: props.isSharing,
      icon: () => <CIcon icon="link" />,
    })
  }

  if (!launchDarkly.value.disable_download_canvas) {
    if (launchDarkly.value.enable_scale_content && props.downloadStatus) {
      if (props.downloadStatus === 'not_started') {
        // Show single download button to start async process
        actionsMenu.push({
          label: () => (
            <div class={FONT_SIZE_CLASS}>
              {t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.actions.downloadPage')}
            </div>
          ),
          key: 'downloadCanvas',
          disabled: !online.value || props.isDownloading,
          icon: () => <CIcon icon={online.value ? 'download' : 'wifi-slash'} />,
        })
      } else if (props.downloadStatus === 'in_progress') {
        // Show loading state
        actionsMenu.push({
          label: () => (
            <div class={FONT_SIZE_CLASS}>
              {t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.actions.downloadPage')} (
              {t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.actions.processing')})
            </div>
          ),
          key: 'downloadCanvas',
          disabled: true,
          icon: () => <CIcon icon="spinner" class="animate-spin" />,
        })
      } else if (props.downloadStatus === 'completed') {
        // Show download options for PDF/PPTX
        const downloadSubmenu: DropdownMixedOption[] = []

        if (props.pdfUrl && !props.isExpired) {
          downloadSubmenu.push({
            label: () => <div class={FONT_SIZE_CLASS}>PDF</div>,
            key: 'downloadCanvas-pdf',
            icon: () => <CIcon icon="file-pdf" />,
          })
        }

        if (props.pptxUrl && !props.isExpired) {
          downloadSubmenu.push({
            label: () => <div class={FONT_SIZE_CLASS}>PPTX</div>,
            key: 'downloadCanvas-pptx',
            icon: () => <CIcon icon="file-powerpoint" />,
          })
        }

        // Add divider before regenerate option only if there are download options
        if (downloadSubmenu.length > 0 && !props.isExpired) {
          downloadSubmenu.push({ key: 'd-regenerate', type: 'divider' })
        }

        // Add regenerate option (always show, but change text based on expiry)
        downloadSubmenu.push({
          label: () => (
            <div class={FONT_SIZE_CLASS}>
              {t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.actions.regenerateFiles')}
            </div>
          ),
          key: 'downloadCanvas',
          icon: () => <CIcon icon="sync" />,
        })

        if (downloadSubmenu.length > 0) {
          actionsMenu.push({
            label: () => (
              <div class={FONT_SIZE_CLASS}>
                {t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.actions.downloadPage')}
              </div>
            ),
            key: 'downloadCanvas',
            children: downloadSubmenu,
            disabled: false,
            icon: () => <CIcon icon="download" />,
          })
        }
      }
    } else {
      // Legacy download behavior
      const downloadSubmenu: DropdownMixedOption[] = [
        {
          label: () => <div class={FONT_SIZE_CLASS}>PDF</div>,
          key: 'downloadCanvas-pdf',
          icon: () => <CIcon icon="file-pdf" />,
        },
      ]

      actionsMenu.push({
        label: () => (
          <div class={FONT_SIZE_CLASS}>
            {t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.actions.downloadPage')}
          </div>
        ),
        key: 'downloadCanvas',
        children: downloadSubmenu,
        disabled: !online.value || props.isDownloading,
        icon: () => <CIcon icon={online.value ? 'download' : 'wifi-slash'} />,
      })
    }
  }

  if (!props.hideViewNewTab) {
    actionsMenu.push({
      label: () => (
        <div class={FONT_SIZE_CLASS}>
          {t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.actions.viewNewTab')}
        </div>
      ),
      key: 'viewNewTab',
      icon: () => <CIcon icon="browsers" />,
    })
  }

  if (!props.hideCopy || !props.hideDelete) {
    actionsMenu.push({ key: 'd1', type: 'divider' })
  }

  if (!props.hideLock) {
    if (isLocked.value && (isLockedByMe.value || isOrgAdmin.value)) {
      // Only show unlock option if the current user is the one who locked it
      actionsMenu.push({
        label: () => (
          <div class={FONT_SIZE_CLASS}>{t('canvasUI.components.templates.CTemplateManagement.actions.unlock')}</div>
        ),
        key: 'unlock',
        icon: () => <CIcon icon="unlock" />,
      })
    } else if (!isLocked.value) {
      actionsMenu.push({
        label: () => (
          <div class={FONT_SIZE_CLASS}>{t('canvasUI.components.templates.CTemplateManagement.actions.lock')}</div>
        ),
        key: 'lock',
        icon: () => <CIcon icon="lock" />,
      })
    }
  }

  if (!props.hideOwnerReassign) {
    actionsMenu.push({
      label: () => (
        <div class={FONT_SIZE_CLASS}>
          {t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.actions.ownerReassign')}
        </div>
      ),
      key: 'ownerReassign',
      disabled: props.isReassigningOwner,
      icon: () => <CIcon icon="solid-share-circle-user" faType="fa-kit" />,
    })
  }

  if (!props.hideCopy) {
    actionsMenu.push({
      label: () => (
        <div class={FONT_SIZE_CLASS}>
          {t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.actions.duplicate')}
        </div>
      ),
      disabled: props.downloadStatus === 'in_progress',
      key: 'copy',
      icon: () => <CIcon icon="copy" />,
    })
  }

  if (!props.hideDelete) {
    actionsMenu.push({
      label: () => (
        <div class={FONT_SIZE_CLASS} style={{ color: themeVars.value.error }}>
          {t('canvasUI.components.savedCanvases.CSavedCanvasesManagement.actions.delete')}
        </div>
      ),
      key: 'delete',
      icon: () => <CIcon icon="trash" color={themeVars.value.error} />,
    })
  }

  return actionsMenu
}
</script>
